// App shell: top bar (with the candy-stripe accent bar) plus the scrollable
// main content area. Auth gates the whole shell. There is no department
// sidebar (this app has one flat tile menu, not Cockpit's department nav),
// so the shell is simpler than Cockpit's: just Header + <Outlet>.

import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Header } from './Header';
import { AuthGate } from './AuthGate';
import { BottomNav } from './BottomNav';
import { ChatBubble } from './ChatBubble';
import { RouteErrorBoundary } from './RouteErrorBoundary';
import { resolveRouteTitle } from './routeTitles';
import { HelpAndSupport } from '@/components/HelpAndSupport';
import { captureSweetSpotScreenshot, type SupportScreenshot } from '@/lib/supportScreenshot';
import { useDocumentTitle } from '@/lib/useDocumentTitle';

export function Layout() {
  // Keyed by pathname so navigating away from a crashed page resets the boundary.
  const { pathname } = useLocation();
  useDocumentTitle(resolveRouteTitle(pathname));
  const [helpOpen, setHelpOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [screenshot, setScreenshot] = useState<SupportScreenshot | null>(null);

  // Capture before the dialog opens, or the dialog itself lands in the picture.
  // When the reader came from the help panel, let it slide out first.
  async function openReport({ closeHelpFirst = false } = {}) {
    if (closeHelpFirst) {
      setHelpOpen(false);
      await new Promise((resolve) => window.setTimeout(resolve, 250));
    }
    setScreenshot(await captureSweetSpotScreenshot());
    setReportOpen(true);
  }
  return (
    <AuthGate>
      <div className="app-shell flex h-screen flex-col overflow-hidden bg-candy-cream text-candy-ink dark:bg-candy-dark-bg dark:text-candy-dark-ink">
        <Header onHelpOpen={() => setHelpOpen(true)} onReportIssueOpen={() => void openReport()} />
        <main data-sweetspot-main className="main-with-bottom-nav min-w-0 flex-1 overflow-y-auto px-4 py-6 sm:px-8">
          <RouteErrorBoundary key={pathname}>
            <Outlet />
          </RouteErrorBoundary>
        </main>
        <HelpAndSupport
          helpOpen={helpOpen}
          onHelpClose={() => setHelpOpen(false)}
          reportOpen={reportOpen}
          onReportOpen={() => void openReport({ closeHelpFirst: true })}
          onReportClose={() => setReportOpen(false)}
          screenshot={screenshot}
        />
        <ChatBubble />
        <BottomNav />
      </div>
    </AuthGate>
  );
}
